import React, { useState, useEffect, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEye,
  faFileUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import RecipientCard from "../components/RecipientCard";
import DocumentUpload from "../components/DocumentUpload";
import AddMessage from "../components/AddMessage";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useCreateTemplateMutation } from "../services/useCreateTemplateMutation";
import { useUploadTemplateDocument } from "../services/useUploadTemplateDocument";
import { toast } from "react-toastify";
import Spinner from "../components/Spinner";
import { showErrorToast } from "../utils/toast";

const CopyTemplatesPage = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { templateData } = state || {};

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
    getValues,
  } = useForm({
    defaultValues: {
      templateName: "",
      templateDescription: "",
      emailSubject: "",
      emailMessage: "",
      recipients: [],
      document: null,
    },
    mode: "onChange",
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "recipients",
  });

  const [isDocumentAccordionOpen, setIsDocumentAccordionOpen] = useState(true);
  const [isRecipientsAccordionOpen, setIsRecipientsAccordionOpen] =
    useState(true);
  const [isMessageAccordionOpen, setIsMessageAccordionOpen] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recipientRequiredError, setRecipientRequiredError] = useState("");

  const {
    mutate: uploadDocument,
    isLoading: isUploadLoading,
    error: uploadError,
  } = useUploadTemplateDocument();

  const {
    mutate: createTemplate,
    isLoading: isCreateTemplateLoading,
    error: createTemplateError,
  } = useCreateTemplateMutation();

  useEffect(() => {
    if (templateData) {
      reset({
        templateName: templateData.name,
        templateDescription: templateData.description,
        emailSubject: templateData.email_subject,
        emailMessage: templateData.email_message,
      });

      if (templateData.signers && templateData.signers.length > 0) {
        templateData.signers.forEach((signer) => {
          append({
            name: signer.name,
            signer_role_name: signer.signer_role_name,
            email: signer.email,
            action: "Needs to Sign",
          });
        });
      }

      if (templateData.document_link) {
        fetch(templateData.document_link)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], "document.pdf", {
              type: "application/pdf",
            });
            setUploadedFile(file);
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
            setValue("document", file);
          })
          .catch((error) => console.error("Error fetching document:", error));
      }
    }
  }, [templateData, reset, append, setValue]);

  const toggleDocumentAccordion = () => {
    setIsDocumentAccordionOpen(!isDocumentAccordionOpen);
  };

  const toggleRecipientsAccordion = () => {
    setIsRecipientsAccordionOpen(!isRecipientsAccordionOpen);
  };

  const toggleMessageAccordion = () => {
    setIsMessageAccordionOpen(!isMessageAccordionOpen);
  };

  const onSubmit = useCallback(
    async (data, isNext = false) => {
      if (fields.length === 0) {
        setRecipientRequiredError("At least one recipient is required.");
        return;
      } else {
        setRecipientRequiredError("");
      }

      try {
        const isValidDocument = await trigger("document");
        const recipientsData = getValues("recipients");

        const isValidRecipients =
          recipientsData &&
          recipientsData.length > 0 &&
          recipientsData.every(
            (recipient) =>
              recipient.name && recipient.email && recipient.signer_role_name
          );
        if (!isValidDocument || !isValidRecipients) {
          return;
        }

        const {
          templateName,
          templateDescription,
          recipients,
          emailSubject,
          emailMessage,
        } = data;

        let uploadedDocumentResponse = null;

        if (uploadedFile) {
          try {
            uploadedDocumentResponse = await new Promise((resolve, reject) => {
              uploadDocument(
                { file: uploadedFile },
                {
                  onSuccess: (data) => {
                    resolve(data);
                  },
                  onError: (error) => {
                    reject(error);
                  },
                }
              );
            });
          } catch (uploadError) {
            console.error("Document upload failed:", uploadError);
            return;
          }
        }

        const createTemplatePayload = {
          name: templateName,
          email_subject: emailSubject,
          description: templateDescription || "",
          email_message: emailMessage,
          signers: recipients.map((recipient, index) => ({
            name: recipient.name,
            email: recipient.email,
            signing_order: index + 1,
            signer_roles: recipient.signer_roles,
            signer_role_name: recipient.signer_role_name,
          })),
          link:
            uploadedDocumentResponse?.data?.url ||
            templateData.document_link ||
            null,
        };

        let createTemplateResponse = null;

        try {
          createTemplateResponse = await new Promise((resolve, reject) => {
            createTemplate(createTemplatePayload, {
              onSuccess: (data) => {
                resolve(data);
              },
              onError: (error) => {
                reject(error);
              },
            });
          });
        } catch (createTemplateError) {
          console.error("Create template failed:", createTemplateError);
          return;
        }

        const AddFieldsInDocumentData = {
          signers: createTemplateResponse?.data?.signersData?.map(
            (recipient) => ({
              name: recipient.name,
              email: recipient.email,
              signing_order: recipient.signing_order,
              signer_role_name: recipient.signer_role_name,
              signer_roles: recipient.signer_roles,
              signer_id: recipient.id,
            })
          ),
          link:
            uploadedDocumentResponse?.data?.url ||
            templateData.document_link ||
            null,
          templateId: createTemplateResponse?.data?.templateId,
        };

        if (!createTemplateResponse?.success) {
          showErrorToast(
            createTemplateResponse?.data?.[0]?.msg || "Error creating template"
          );
          return;
        }

        if (isNext) {
          toast.success("Template created successfully");
          navigate("/send-template", {
            state: { data: AddFieldsInDocumentData },
          });
        } else {
          toast.success("Template created successfully");
          navigate("/");
        }
      } catch (error) {
        console.error("Error during form submission:", error);
      } finally {
      }
    },
    [
      fields,
      navigate,
      createTemplate,
      uploadDocument,
      trigger,
      uploadedFile,
      templateData,
      getValues,
    ]
  );

  const handleClear = () => {
    reset();
    setUploadedFile(null);
    setPreviewUrl(null);
    setValue("document", null);
    setRecipientRequiredError("");
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDiscard = () => {
    handleClear();
    navigate("/");
  };

  const handleSaveAndClose = async () => {
    const isValid = await trigger();
    if (fields.length === 0) {
      setRecipientRequiredError("At least one recipient is required.");
      return;
    } else {
      setRecipientRequiredError("");
    }
    handleSubmit((data) => onSubmit(data, false))();
    handleCloseModal();
  };

  const handleNext = async () => {
    if (fields.length === 0) {
      setRecipientRequiredError("At least one recipient is required.");
      return;
    } else {
      setRecipientRequiredError("");
    }
    const isValid = await trigger();
    handleSubmit((data) => onSubmit(data, true))();
  };

  const onMoveRecipient = useCallback(
    (dragIndex, hoverIndex) => {
      move(dragIndex, hoverIndex);
    },
    [move]
  );

  const handleRemoveRecipient = async (index) => {
    remove(index);

    await trigger(["recipients"]);
  };

  return (
    <>
      {(isUploadLoading || isCreateTemplateLoading) && <Spinner />}
      <DndProvider backend={HTML5Backend}>
        <div className="min-h-screen flex flex-col bg-gray-100">
          <div className="sticky top-0 bg-white shadow-md p-4 flex justify-between items-center z-10">
            <h2 className="text-lg font-semibold">Copy Template</h2>
            <div className="flex items-center">
              <button
                data-tooltip-id="closeTooltip"
                onClick={handleOpenModal}
                className="focus:outline-none rounded-full p-2 hover:bg-gray-200 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
              </button>
              <Tooltip
                id="closeTooltip"
                content="Close"
                place="bottom"
                style={{ zIndex: 10000 }}
              />
            </div>
          </div>

          <div className="container mx-auto p-4 md:p-8 flex-grow">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="max-w-2xl mx-auto space-y-6">
                <div>
                  <label
                    htmlFor="templateName"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Template Name
                  </label>
                  <input
                    type="text"
                    id="templateName"
                    placeholder="Enter template name"
                    {...register("templateName", {
                      required: "Template name is required",
                      validate: (value) => {
                        if (value.trim() === "") {
                          return "Template name cannot be empty or just spaces";
                        }
                        return true;
                      },
                    })}
                    className="shadow appearance-none ring-1 border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#cfc9ff]"
                  />
                  {errors.templateName && (
                    <p className="text-red-500 text-xs italic">
                      {errors.templateName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="templateDescription"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Template Description
                  </label>
                  <textarea
                    id="templateDescription"
                    placeholder="Description (Optional)"
                    {...register("templateDescription")}
                    className="shadow appearance-none border ring-1 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] h-24"
                  />
                </div>
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                  onClick={toggleDocumentAccordion}
                >
                  <span>Add Document</span>
                  <span>{isDocumentAccordionOpen ? "-" : "+"}</span>
                </button>
                {isDocumentAccordionOpen && (
                  <div className="p-6">
                    <DocumentUpload
                      uploadedFile={uploadedFile}
                      setUploadedFile={setUploadedFile}
                      previewUrl={previewUrl}
                      setPreviewUrl={setPreviewUrl}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                    />
                  </div>
                )}
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                  onClick={toggleRecipientsAccordion}
                >
                  <span>Add Recipients</span>
                  <span>{isRecipientsAccordionOpen ? "-" : "+"}</span>
                </button>
                {isRecipientsAccordionOpen && (
                  <div className="p-6">
                    {fields.map((field, index) => (
                      <RecipientCard
                        key={field.id}
                        recipient={field}
                        index={index}
                        remove={handleRemoveRecipient}
                        register={register}
                        errors={errors}
                        moveRecipient={onMoveRecipient}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        append({
                          name: "",
                          role: "",
                          email: "",
                          action: "Needs to Sign",
                        })
                      }
                      className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline "
                      // className="mr-2 py-2 px-4 rounded font-semibold border-2 border-[#cfc9ff] text-[#cfc9ff] hover:bg-[#cfc9ff] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] focus:ring-opacity-50 transition-colors duration-300"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add
                      Recipient
                    </button>
                    {recipientRequiredError && (
                      <p className="text-red-500 text-xs italic">
                        {recipientRequiredError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                  onClick={toggleMessageAccordion}
                >
                  <span>Add Message</span>
                  <span>{isMessageAccordionOpen ? "-" : "+"}</span>
                </button>
                {isMessageAccordionOpen && (
                  <div className="p-6">
                    <AddMessage
                      register={register}
                      errors={errors}
                      uploadedFile={uploadedFile}
                      watch={watch}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="sticky bottom-0 bg-white shadow-md p-4 flex justify-end items-center z-10 space-x-2">
            {/* <button
              type="button"
              onClick={handleSaveAndClose}
              className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline "
              // disabled={!isValid || isUploadLoading || isCreateTemplateLoading}
              // className="mr-2 py-2 px-4 rounded font-semibold border-2 border-[#cfc9ff] text-[#cfc9ff] hover:bg-[#cfc9ff] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] focus:ring-opacity-50 transition-colors duration-300 "
            >
              Save and Close
            </button> */}
            {uploadError && (
              <p className="text-red-500 text-xs italic">
                Error uploading document. Please try again.
              </p>
            )}
            {createTemplateError && (
              <p className="text-red-500 text-xs italic">
                Error creating template. Please try again.
              </p>
            )}
            <button
              type="button"
              onClick={handleNext}
              // disabled={!isValid}
              className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline "
            >
              Next
            </button>
          </div>

          {isModalOpen && (
            <div className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <h2 className="text-lg font-semibold mb-4">
                  Discard your changes?
                </h2>
                <p className="text-gray-700 mb-4">
                  Do you want to discard changes to this template?
                </p>
                <div className="mt-4 flex justify-end space-x-4">
                  <button
                    onClick={handleDiscard}
                    className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  >
                    Discard Changes
                  </button>
                  {/* <button
                    onClick={handleSaveAndClose}
                    className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  >
                    Save
                  </button> */}
                </div>
              </div>
            </div>
          )}
        </div>
      </DndProvider>
    </>
  );
};

export default CopyTemplatesPage;
